import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { useAuth, hasRole } from "../../context/AuthContext";
import {
  listTimeEntries,
  addTimeEntry,
  deleteTimeEntry,
  setTimeEntryStatus,
  listUsers,
} from "../../lib/firebase";
import { entriesToCsv, downloadCsv } from "../../utils/csv";
import { createInvoice } from "../../lib/functions";
import TimeTrackerCli from "./TimeTrackerCli";
import Instructions from "../../components/Instructions/Instructions";

const SectionStyled = styled.section`
  padding: 96px 0;
  background: var(--nd-black);
`;

const Label = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 16px;
`;

const Title = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 48px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 12px;
  @media (max-width: 768px) { font-size: 32px; }
`;

const Subtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 32px;
`;

const AdminBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: var(--nd-surface-raised);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  flex-wrap: wrap;
`;

const AdminLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const UserSelect = styled.select`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  background: var(--nd-surface);
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  padding: 6px 10px;
  outline: none;
  min-width: 240px;
`;

const ExportBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;
  outline: none;
  &:hover { border-color: var(--nd-text-display); color: var(--nd-text-display); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 2fr 2fr 80px auto;
  gap: 12px;
  margin-bottom: 32px;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const Input = styled.input`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  color: var(--nd-text-primary);
  padding: 10px 12px;
  outline: none;
  &:focus { border-color: var(--nd-text-display); }
`;

const Button = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: var(--nd-text-display);
  color: var(--nd-black);
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  text-align: left;
  padding: 12px 8px;
  border-bottom: 1px solid var(--nd-border);
`;

const Td = styled.td`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  padding: 12px 8px;
  border-bottom: 1px solid var(--nd-border);
`;

const HoursTd = styled(Td)`
  font-family: 'Space Mono', monospace;
  text-align: right;
`;

const DeleteBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  background: transparent;
  border: 1px solid var(--nd-border);
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  outline: none;
  &:hover {
    color: var(--nd-accent);
    border-color: var(--nd-accent);
  }
`;

const Empty = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  padding: 48px 0;
  text-align: center;
`;

const handleExport = (entries, viewedUid, allUsers, currentUser, approvedOnly) => {
  const target = allUsers.find((u) => u.id === viewedUid);
  const email = (target && target.email) || (currentUser && currentUser.email) || viewedUid;
  const month = new Date().toISOString().slice(0, 7);
  const filename = `time-${email.replace(/[^a-z0-9._-]+/gi, "_")}-${month}.csv`;
  const filtered = approvedOnly ? entries.filter((e) => e.status === "approved") : entries;
  downloadCsv(filename, entriesToCsv(filtered));
};

const StatusBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid ${({ status }) =>
    status === "approved" ? "rgba(74,158,92,0.4)" :
    status === "submitted" ? "rgba(212,168,67,0.4)" :
    status === "rejected" ? "rgba(215,25,33,0.4)" :
    "var(--nd-border-visible)"};
  background: ${({ status }) =>
    status === "approved" ? "rgba(74,158,92,0.15)" :
    status === "submitted" ? "rgba(212,168,67,0.15)" :
    status === "rejected" ? "rgba(215,25,33,0.15)" :
    "transparent"};
  color: ${({ status }) =>
    status === "approved" ? "var(--nd-success)" :
    status === "submitted" ? "var(--nd-warning)" :
    status === "rejected" ? "var(--nd-accent)" :
    "var(--nd-text-disabled)"};
`;

const ActionBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--nd-border);
  background: transparent;
  color: var(--nd-text-secondary);
  cursor: pointer;
  outline: none;
  margin-right: 4px;
  &:hover { color: var(--nd-text-display); border-color: var(--nd-text-display); }
`;

const ApproveBtn = styled(ActionBtn)`
  &:hover { color: var(--nd-success); border-color: var(--nd-success); }
`;

const RejectBtn = styled(ActionBtn)`
  &:hover { color: var(--nd-accent); border-color: var(--nd-accent); }
`;

const BulkBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const ApprovedOnlyLabel = styled.label`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const TimeTracker = () => {
  const { user, role } = useAuth();
  const canWrite = hasRole(role, "staff");
  const isAdmin = hasRole(role, "admin");

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ date: "", project: "", task: "", hours: "" });

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [taxRate, setTaxRate] = useState(0);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const viewedUid = selectedUid || (user && user.uid);
  const viewingOther = isAdmin && selectedUid && user && selectedUid !== user.uid;

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    listUsers().then((rows) => {
      if (!cancelled) setAllUsers(rows);
    });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    if (!viewedUid) return;
    let cancelled = false;
    setLoading(true);
    listTimeEntries(viewedUid).then((rows) => {
      if (cancelled) return;
      setEntries(rows);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [viewedUid]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (viewingOther) return;
    if (!canWrite || !user) return;
    if (!draft.date || !draft.project) return;
    const entry = {
      date: draft.date,
      project: draft.project,
      task: draft.task,
      hours: Number(draft.hours) || 0,
      notes: "",
    };
    const id = await addTimeEntry(user.uid, entry);
    setEntries((prev) => [{ id, ...entry }, ...prev]);
    setDraft({ date: "", project: "", task: "", hours: "" });
  };

  const handleDelete = async (id) => {
    if (viewingOther) return;
    if (!canWrite || !user) return;
    await deleteTimeEntry(user.uid, id);
    setEntries((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSetStatus = async (id, status) => {
    const targetUid = viewedUid;
    setEntries((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
    try {
      await setTimeEntryStatus(targetUid, id, status);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[time-tracker] status change failed:", err);
    }
  };

  const handleSubmitAll = async () => {
    if (viewingOther || !user) return;
    const drafts = entries.filter((e) => (e.status || "draft") === "draft");
    if (drafts.length === 0) return;
    setEntries((prev) => prev.map((r) => (r.status || "draft") === "draft" ? { ...r, status: "submitted" } : r));
    await Promise.all(drafts.map((d) => setTimeEntryStatus(user.uid, d.id, "submitted").catch(() => {})));
  };

  const handleCreateInvoice = async () => {
    if (!viewedUid) return;
    setCreatingInvoice(true);
    setInvoiceResult(null);
    try {
      // Last full month per spec.
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const period = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}`;
      const res = await createInvoice({ uid: viewedUid, period, taxRate });
      setInvoiceResult(res);
    } catch (err) {
      setInvoiceResult({ error: err.message || "failed to create invoice" });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const showAddForm = canWrite && !viewingOther;
  const showDelete = canWrite && !viewingOther;
  const draftsCount = entries.filter((e) => (e.status || "draft") === "draft").length;

  return (
    <SectionStyled>
      <Container>
        <Label>TIME TRACKER</Label>
        <Title>Your hours</Title>
        <Subtitle>
          {viewingOther
            ? "Viewing another user. Read-only."
            : canWrite
              ? "Log entries below. Stored in Firestore under your account."
              : "Read-only. Staff can add or remove entries."}
        </Subtitle>

        <Instructions label="how to use time tracker">
          <h4>What this page does</h4>
          <p>
            Logs the hours you work. At the end of the month, those hours
            turn into an invoice the client pays.
          </p>

          <h4>The end-of-day habit</h4>
          <ol>
            <li>Before closing your laptop, add a row: today's date, hours worked, which client/project, one sentence on what you did.</li>
            <li>The row saves as a draft. You can edit it tomorrow if you remember more.</li>
            <li>End of week, double-check the rows. Then mark them as submitted.</li>
            <li>An admin reviews and approves. Approved hours go into your next invoice.</li>
          </ol>

          <h4>Use case · "I had a 4-hour meeting + 2 hours of debugging"</h4>
          <p>
            Two rows. One for the meeting (note: "client architecture
            review"). One for the bug fix (note: "ECS task crash, fixed
            health check path"). Specific notes help the client see value.
          </p>

          <h4>Use case · "I forgot to log Friday's work"</h4>
          <p>
            Add the row Monday with last Friday's date. The system doesn't
            care when you write it, only what date the work happened on.
          </p>

          <h4>Use case · "Terminal user, hate clicking"</h4>
          <p>
            Above the table there's a CLI snippet. Copy it, install once,
            then type <code>dioscuro log 2h "fix prod alert"</code> from
            anywhere. Same effect as filling the form.
          </p>

          <h4>If you're an admin</h4>
          <p>
            Use the Viewing dropdown to look at another teammate's hours.
            You can approve, reject, export to CSV, or generate an
            invoice straight from approved rows.
          </p>
        </Instructions>

        <TimeTrackerCli />

        {isAdmin && allUsers.length > 0 && user && (
          <AdminBar>
            <AdminLabel>Viewing</AdminLabel>
            <UserSelect
              value={selectedUid || user.uid}
              onChange={(e) => setSelectedUid(e.target.value === user.uid ? null : e.target.value)}
            >
              {allUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email || u.id}{u.id === user.uid ? "  (you)" : ""}
                </option>
              ))}
            </UserSelect>
            <ExportBtn
              type="button"
              disabled={entries.length === 0}
              onClick={() => handleExport(entries, viewedUid, allUsers, user, approvedOnly)}
            >
              ↓ Export CSV
            </ExportBtn>
            <ApprovedOnlyLabel>
              <input
                type="checkbox"
                checked={approvedOnly}
                onChange={(e) => setApprovedOnly(e.target.checked)}
              />
              Approved only
            </ApprovedOnlyLabel>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={(taxRate * 100).toFixed(2)}
              onChange={(e) => setTaxRate(Number(e.target.value) / 100)}
              style={{ width: 60, padding: "4px 8px" }}
              title="Tax rate %"
            />
            <AdminLabel>% tax</AdminLabel>
            <ExportBtn
              type="button"
              disabled={creatingInvoice || !viewedUid}
              onClick={handleCreateInvoice}
            >
              {creatingInvoice ? "Creating…" : "💰 Create invoice"}
            </ExportBtn>
            {invoiceResult && (
              <span style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.06em",
                color: invoiceResult.error ? "var(--nd-accent)" : "var(--nd-success)",
              }}>
                {invoiceResult.error ? (
                  `Error: ${invoiceResult.error}`
                ) : (
                  <>
                    ✓ {invoiceResult.invoiceNumber} · ${invoiceResult.total.toFixed(2)} ·{" "}
                    <a
                      href={invoiceResult.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--nd-interactive, var(--nd-success))" }}
                    >
                      download PDF
                    </a>
                  </>
                )}
              </span>
            )}
          </AdminBar>
        )}

        {!viewingOther && canWrite && draftsCount > 0 && (
          <BulkBar>
            <ActionBtn type="button" onClick={handleSubmitAll}>
              Submit all drafts ({draftsCount})
            </ActionBtn>
          </BulkBar>
        )}

        {showAddForm && (
          <Form onSubmit={handleAdd}>
            <Field>
              <FieldLabel>Date</FieldLabel>
              <Input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Project</FieldLabel>
              <Input
                type="text"
                placeholder="Client — engagement"
                value={draft.project}
                onChange={(e) => setDraft((d) => ({ ...d, project: e.target.value }))}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Task</FieldLabel>
              <Input
                type="text"
                placeholder="What you did"
                value={draft.task}
                onChange={(e) => setDraft((d) => ({ ...d, task: e.target.value }))}
              />
            </Field>
            <Field>
              <FieldLabel>Hours</FieldLabel>
              <Input
                type="number"
                step="0.25"
                min="0"
                value={draft.hours}
                onChange={(e) => setDraft((d) => ({ ...d, hours: e.target.value }))}
              />
            </Field>
            <Button type="submit">Log entry</Button>
          </Form>
        )}

        {loading ? (
          <Empty>Loading…</Empty>
        ) : entries.length === 0 ? (
          <Empty>No entries yet</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Project</Th>
                <Th>Task</Th>
                <Th style={{ textAlign: "right" }}>Hours</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => {
                const rowStatus = row.status || "draft";
                return (
                  <tr key={row.id}>
                    <Td>{row.date}</Td>
                    <Td>{row.project}</Td>
                    <Td>{row.task}</Td>
                    <HoursTd>{Number(row.hours || 0).toFixed(1)}</HoursTd>
                    <Td><StatusBadge status={rowStatus}>{rowStatus}</StatusBadge></Td>
                    <Td style={{ textAlign: "right" }}>
                      {!viewingOther && canWrite && rowStatus === "draft" && (
                        <ActionBtn type="button" onClick={() => handleSetStatus(row.id, "submitted")}>Submit</ActionBtn>
                      )}
                      {viewingOther && isAdmin && rowStatus === "submitted" && (
                        <>
                          <ApproveBtn type="button" onClick={() => handleSetStatus(row.id, "approved")}>Approve</ApproveBtn>
                          <RejectBtn type="button" onClick={() => handleSetStatus(row.id, "rejected")}>Reject</RejectBtn>
                        </>
                      )}
                      {showDelete && (
                        <DeleteBtn type="button" onClick={() => handleDelete(row.id)}>
                          Delete
                        </DeleteBtn>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Container>
    </SectionStyled>
  );
};

export default TimeTracker;
